import { Component, MouseEvent, ReactNode, ReactElement } from 'react'
import * as cn from 'classnames'
import * as find from 'lodash/find'
import * as styles from 'styles/molecules/MoneyflowTracker.css'
import { MoneyflowType, MoneyflowTrackerSeverity, ExtendedMoneyflowTracker } from 'interfaces/moneyflow'
import MoneyflowTrackerChart from 'components/atoms/charts/MoneyflowTrackerChart'
import Button from 'components/features/pfm/Button/Button'
import ExpandButton from 'components/atoms/buttons/ExpandButton'
import { formatUSCurrency as f } from 'helpers/formatHelpers'
import { CategoryId, Category } from 'interfaces/meta'
import { getTrackerCategoryName } from 'helpers/categoriesHelpers'
import TextButton from 'components/atoms/buttons/TextButton'
import { NavigationLink } from 'components/atoms/navigation/NavigationLink'
import { ALL_CATEGORY_ID } from 'constants/meta'
import Touchable from 'components/utils/Touchable'
import SvgIcon from 'components/atoms/SvgIcon'

export type MoneyflowTrackerProps = {
  tracker: ExtendedMoneyflowTracker
  isExpanded: boolean
  category: Category

  toggleTrackerDetails: (categoryId: CategoryId | null) => void
  onClickView: () => void
  onClickEdit: () => void
}

type TrackerDetailsDescription = {
  type: MoneyflowType
  severity: MoneyflowTrackerSeverity
  getEmphasis: (tracker: ExtendedMoneyflowTracker) => ReactElement<{}>
  getText: (tracker: ExtendedMoneyflowTracker) => ReactElement<{}>
}

const { abs } = Math

const TRACKER_DETAILS_DESCRIPTIONS: TrackerDetailsDescription[] = [
  {
    type: 'spending',
    severity: 'warning',
    getEmphasis: ({ projection, limit }) => (
      <div>
        Projected to exceed your limit by
        <span className={ styles.value }> { f(abs(projection - limit)) }</span>.
      </div>
    ),
    getText: ({ limit, current }) => (
      <div>
        <span className={ styles.value }>{ f(current) } </span>spent,
        <span className={ styles.value }> { f(abs(current - limit)) } </span>until your limit.
      </div>
    ),
  },
  {
    type: 'spending',
    severity: 'on-track',
    getEmphasis: () => (<div>On Track.</div>),
    getText: ({ limit, current }) => (
      <div>
        <span className={ styles.value }>{ f(current) } </span>spent,
        <span className={ styles.value }> { f(abs(current - limit)) } </span>until your limit.
      </div>
    ),
  },
  {
    type: 'spending',
    severity: 'danger',
    getEmphasis: ({ current, limit }) => (
      <div>
        Over your limit by
        <span className={ styles.value }> { f(abs(current - limit)) }</span>.
      </div>
    ),
    getText: ({ current }) => (
      <div>
        <span className={ styles.value }>{ f(current) }</span> spent.
      </div>
    ),
  },
  {
    type: 'income',
    severity: 'warning',
    getEmphasis: ({ projection, limit }) => (
      <div>
        Projected to miss your goal by
        <span className={ styles.value }> { f(abs(projection - limit)) }</span>.
      </div>
    ),
    getText: ({ current, limit }) => (
      <div>
        <span className={ styles.value }>{ f(current) } </span>earned,
        <span className={ styles.value }> { f(abs(current - limit)) } </span>until your goal.
      </div>
    ),
  },
  {
    type: 'income',
    severity: 'on-track',
    getEmphasis: () => (<div>On Track.</div>),
    getText: ({ current, limit }) => (
      current > limit ? (
        <div>
          <span className={ styles.value }>{ f(current) } </span>earned,
          you exceeded the goal by <span className={ styles.value }>{ f(current - limit) }</span>.
        </div>
      ) : (
        <div>
          <span className={ styles.value }>{ f(current) } </span>earned,
          <span className={ styles.value }> { f(abs(current - limit)) } </span>until your goal.
        </div>
      )
    ),
  },
]

function getTrackerDetails (tracker: ExtendedMoneyflowTracker, type: MoneyflowType):
  { emphasis: ReactNode, text: ReactNode } {
  const { severity } = tracker

  const trackerDetails = find(TRACKER_DETAILS_DESCRIPTIONS, { severity, type })

  if (!trackerDetails) {
    return {
      emphasis: '',
      text: '',
    }
  }

  return {
    emphasis: trackerDetails.getEmphasis(tracker),
    text: trackerDetails.getText(tracker),
  }
}

class MoneyflowTracker extends Component<MoneyflowTrackerProps, {}> {
  private toggleZoneNode: HTMLElement | null = null

  render () {
    const {
      isExpanded,
      category,
      tracker,
      onClickEdit,
      onClickView,
    } = this.props

    const { category: categoryId, severity, type } = tracker
    const { emphasis, text } = getTrackerDetails(tracker, type)

    const categoryName = getTrackerCategoryName(category, type)

    let iconKind = categoryId
    if (categoryId === ALL_CATEGORY_ID) {
      iconKind = type === 'spending' ? 'spending' : 'income'
    }

    const iconSource = require(`icons/${ iconKind }.svg`).default

    const trackerClass = cn(styles.moneyflowTracker, {
      [styles.danger]: severity === 'danger',
      [styles.warning]: severity === 'warning',
      [styles.onTrack]: severity === 'on-track',
      [styles.expanded]: isExpanded,
    })

    return (
      <div className={ trackerClass }>
        <Touchable
          onTap={ this.onTap }
          onClick={ this.onClick }
        >
          <div className={ styles.moneyflowTrackerInner }>
            <div
              className={ styles.tracker }
              ref={ (node) => { this.toggleZoneNode = node } }
            >
              <div className={ styles.nameWrapper }>
                <div className={ styles.trackerIcon }>
                  <SvgIcon src={ iconSource } />
                </div>
                <div className={ styles.name }>{ categoryName }</div>
              </div>
              <div className={ styles.trackerDetails }>
                <div className={ styles.emphasis }>{ emphasis }</div>
                <div className={ styles.text }>{ text }</div>
              </div>
              <ExpandButton isExpanded={ isExpanded } />
            </div>
            <div className={ styles.trackerInner }>
              <MoneyflowTrackerChart { ...tracker } />
            </div>
          </div>
        </Touchable>
        {
          /*
          TODO @pmyagkov `NavigationLink` is put out of `Touchable` to avoid mute of a click
          because it doesn't react on a tap event
          */
        }
        <span className={ styles.actionLinks }>
          <TextButton size='regular' onClick={ onClickEdit }>
            { `Change ${ type === 'spending' ? 'limit' : 'goal' }` }
          </TextButton>
          <span className={ styles.linkWrapper }>
            <NavigationLink
              to={ type === 'spending' ? 'spending_breakdown' : 'income_breakdown' }
              onClick={ onClickView }
            >
              <Button size='mini' style='hollow'>
                { `View ${ type === 'spending' ? 'spending' : 'income' }` }
              </Button>
            </NavigationLink>
          </span>
        </span>
      </div>
    )
  }

  private onTap = (e: HammerInput) => {
    const { category, isExpanded, toggleTrackerDetails } = this.props

    if (!isExpanded) {
      toggleTrackerDetails(category.id)
    } else {
      if (e.target && this.toggleZoneNode && this.toggleZoneNode.contains(e.target)) {
        toggleTrackerDetails(null)
      }
    }
  }

  private onClick = (_e: MouseEvent<HTMLElement>) => {
    const { category, toggleTrackerDetails, isExpanded } = this.props
    toggleTrackerDetails(isExpanded ? null : category.id)
  }
}

export default MoneyflowTracker
